import { useMemo } from 'react';
import { Globe, Link2, Trash2 } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import ToggleSwitch from '../BaseComponents/Toggle/ToggleSwitch';
import SelectBox from '../BaseComponents/SelectBox/SelectBox';
import BoardCodeDropdown from '../Booking/BoardCodeDropdown';
import DestinationPicker, { destKey, destLabel } from '../../screens/CMS/Layout/HomepageSettings/components/DestinationPicker';
import TravelIconPicker from '../../screens/CMS/Layout/HomepageSettings/components/TravelIconPicker';
import { isEmoji } from '../../screens/CMS/Layout/HomepageSettings/components/travelIcons';
import {
  MAX_MENU_ITEMS, MAX_SUB, MAX_URL, moveItem, newDestinationItem,
} from './siteHeaderModel';
import { WEBSITE_DEFAULTS } from './headerDefaults';
import { DESTINATION_ROW_ICON } from './headerIcons';
import { destinationLinkPreview, destinationRows } from './headerPreviewModel';
import {
  BilingualInput, BuiltInTile, FieldError, FooterLinkEditor, RowActions, SectionCard, Segmented,
  SwitchRow, TextField, useFieldError, useHeaderEditor,
} from './editorControls';
import styles from './HeaderEditor.module.css';

const PATH = 'menus.destinations';

const clampMax = (v) => Math.min(MAX_MENU_ITEMS, Math.max(1, parseInt(v, 10) || 1));

/** "Website shows": the rows the menu will list, as chips. */
export function WebsiteShows({ rows, enabled, emptyText, loading }) {
  return (
    <div className={styles.websiteShows}>
      <span className={styles.fieldLabel}>Website shows</span>
      {!enabled ? (
        <span className={styles.muted}>Menu switched off</span>
      ) : loading ? (
        <span className={styles.muted}>Loading…</span>
      ) : rows.length === 0 ? (
        <span className={styles.muted}>{emptyText}</span>
      ) : (
        <div className={styles.chips}>
          {rows.map((r) => (
            <span key={r.key} className={styles.chipBlue} title={r.url || ''}>
              {r.label || 'No label'}
              {r.sub ? <em> · {r.sub}</em> : null}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function DestinationRow({ item, index, count, onPatch, onMove, onRemove, holidayTypeOptions }) {
  const base = `${PATH}.items.${item.id}`;
  const rowError = useFieldError(base);
  const url = destinationLinkPreview(item);
  const title = item.label?.en || item.label?.nl || (item.dest ? destLabel(item.dest) : 'New row');
  return (
    <div className={`${styles.itemCard} ${item.active ? '' : styles.itemCardOff} ${rowError ? styles.itemCardError : ''}`}>
      <div className={styles.itemTop}>
        <span className={styles.orderIndex}>{index + 1}</span>
        <TravelIconPicker
          kind="emoji"
          label="Row icon"
          value={isEmoji(item.icon) ? item.icon : ''}
          autoTile={(size) => <BuiltInTile icon={DESTINATION_ROW_ICON} size={size} />}
          autoLabel="Map pin"
          autoHint="The website's map pin"
          onChange={(icon) => onPatch({ icon })}
        />
        <div className={styles.itemTitle}>
          <strong>{title}</strong>
          <span>{item.active ? (url ? `→ ${url}` : 'Not linked yet') : 'Hidden: switched off'}</span>
        </div>
        <span className={styles.toggleWrap} title={item.active ? 'Shown in the menu' : 'Hidden from the menu'}>
          <ToggleSwitch checked={item.active} onChange={() => onPatch({ active: !item.active })} />
        </span>
        <RowActions index={index} count={count} onMove={onMove} onRemove={onRemove} removeTitle="Remove row" />
      </div>

      <div className={styles.grid2}>
        <BilingualInput
          label="Label"
          value={item.label}
          defaults={item.dest?.name || undefined}
          placeholder="e.g. Turkey"
          errorPath={`${base}.label`}
          onChange={(label) => onPatch({ label })}
          compact
        />
        <BilingualInput
          label="Sub-label"
          value={item.sub}
          maxLength={MAX_SUB}
          placeholder="e.g. All inclusive"
          onChange={(sub) => onPatch({ sub })}
          compact
        />
      </div>

      <div className={styles.grid4}>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Place</label>
          {item.dest ? (
            <span className={styles.destChip}>
              <span className={styles.destKind}>{item.dest.type === 'country' ? 'Country' : 'City'}</span>
              <span className={styles.destName}>{destLabel(item.dest)}</span>
              <span className={styles.destCode}>{item.dest.code}</span>
              <button type="button" className={styles.destClear} title="Clear place" aria-label="Clear place" onClick={() => onPatch({ dest: null })}>
                <Trash2 size={12} />
              </button>
            </span>
          ) : (
            <DestinationPicker onPick={(dest) => onPatch({ dest })} />
          )}
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Board type</label>
          <BoardCodeDropdown
            value={item.boardCode || ''}
            placeholder="Any board"
            onChange={(code) => onPatch({ boardCode: String(code || '').toUpperCase() })}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Holiday type</label>
          <SelectBox
            width="100%"
            value={item.holidayTypeId != null ? String(item.holidayTypeId) : ''}
            options={[{ value: '', label: 'Any holiday type' }, ...holidayTypeOptions]}
            placeholder="Any holiday type"
            onChange={(e) => onPatch({ holidayTypeId: e.target.value === '' ? null : Number(e.target.value) })}
          />
        </div>
        <TextField
          label="Custom link (optional)"
          value={item.url}
          placeholder="Overrides the search"
          maxLength={MAX_URL}
          errorPath={`${base}.url`}
          onChange={(value) => onPatch({ url: value })}
        />
      </div>
      <FieldError path={base} />
    </div>
  );
}

/**
 * The Destinations dropdown. Either the homepage's popular destinations (CMS -> Homepage, in
 * that order) or a hand-picked list where each row searches a place, a board type and/or a
 * holiday type, or opens its own link.
 */
export default function DestinationsMenuSection({ value, set, patch, homepageGroups, homepageLoading, errorCount }) {
  const { showToast } = useToast();
  const { lang, holidayTypes } = useHeaderEditor();
  const menu = value;
  const items = useMemo(() => menu.items || [], [menu.items]);
  const manual = menu.source === 'manual';
  const onPatch = (p) => patch(PATH, p);
  // Functional, so two changes in one event never overwrite each other.
  const updateItems = (fn) => set(`${PATH}.items`, (list) => fn(list || []));

  const holidayTypeOptions = useMemo(
    () => (holidayTypes || []).map((t) => ({ value: String(t.id), label: t.name })),
    [holidayTypes]
  );
  const pickedKeys = useMemo(() => new Set(items.filter((it) => it.dest).map((it) => destKey(it.dest))), [items]);
  const rows = destinationRows(menu, homepageGroups, lang);

  const full = items.length >= MAX_MENU_ITEMS;
  const refuseFull = () => showToast(`The menu holds at most ${MAX_MENU_ITEMS} rows`, 'error');

  const addPlace = (dest) => {
    if (full) return refuseFull();
    updateItems((list) => [...list, newDestinationItem(list.map((it) => it.id), { dest })]);
    showToast(`${destLabel(dest)} added to the Destinations menu`, 'success');
    return undefined;
  };

  const addLinkRow = () => {
    if (full) return refuseFull();
    updateItems((list) => [...list, newDestinationItem(list.map((it) => it.id))]);
    showToast('Row added. Give it a label and a link.', 'success');
    return undefined;
  };

  const removeItem = (index) => {
    const it = items[index];
    const name = it?.label?.en || it?.label?.nl || (it?.dest ? destLabel(it.dest) : 'Row');
    updateItems((list) => list.filter((_, i) => i !== index));
    showToast(`${name} removed from the Destinations menu`, 'success');
  };

  return (
    <SectionCard
      id="menu-destinations"
      nested
      icon={Globe}
      tone="blue"
      title="Destinations menu"
      description="A dropdown of places to go."
      enabled={menu.enabled}
      onToggle={(enabled) => onPatch({ enabled })}
      showOn={menu.showOn}
      onShowOn={(showOn) => onPatch({ showOn })}
      errorCount={errorCount}
      previewTarget="menu:destinations"
    >
      <div className={styles.grid3Wide}>
        <BilingualInput
          label="Menu label"
          value={menu.label}
          defaults={WEBSITE_DEFAULTS.destinationsLabel}
          onChange={(label) => onPatch({ label })}
        />
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Filled from</label>
          <Segmented
            value={menu.source}
            ariaLabel="Destinations source"
            options={[
              { value: 'homepage', label: 'Homepage groups' },
              { value: 'manual', label: 'Hand-picked' },
            ]}
            onChange={(source) => onPatch({ source })}
          />
        </div>
        <TextField
          label="Max rows shown"
          type="number"
          value={menu.maxItems}
          inputProps={{ min: 1, max: MAX_MENU_ITEMS }}
          onChange={(v) => onPatch({ maxItems: clampMax(v) })}
        />
      </div>

      {!manual && (
        <>
          <SwitchRow
            title="Group title as sub-label"
            description="Each row shows the homepage group it comes from, e.g. Turkey · Sun destinations."
            checked={menu.showGroupAsSub}
            onChange={(showGroupAsSub) => onPatch({ showGroupAsSub })}
          />
          <p className={styles.hint}>
            The rows are the linked entries of CMS → Homepage → Most popular destinations, in that order.
            Entries without a place, board or holiday type are left out.
          </p>
        </>
      )}

      {manual && (
        <div className={styles.itemList}>
          {items.length === 0 && <p className={styles.emptyNote}>No rows yet. Search a place below to add the first one.</p>}
          {items.map((it, index) => (
            <DestinationRow
              key={it.id}
              item={it}
              index={index}
              count={items.length}
              holidayTypeOptions={holidayTypeOptions}
              onPatch={(p) => updateItems((list) => list.map((x) => (x.id === it.id ? { ...x, ...p } : x)))}
              onMove={(i, dir) => updateItems((list) => moveItem(list, i, dir))}
              onRemove={removeItem}
            />
          ))}
          <div className={styles.addRow}>
            <div className={styles.addRowPicker}>
              <DestinationPicker onPick={addPlace} disabled={full} excludeKeys={pickedKeys} />
            </div>
            <button type="button" className={styles.secondaryBtn} onClick={addLinkRow} disabled={full}>
              <Link2 size={14} /> Add a link row
            </button>
          </div>
          <span className={styles.hint}>{items.length} / {MAX_MENU_ITEMS} rows. Rows past the max are kept but not shown.</span>
        </div>
      )}

      <WebsiteShows
        rows={rows}
        enabled={menu.enabled}
        loading={!manual && homepageLoading}
        emptyText={manual ? 'Nothing yet: the menu is left out of the bar' : 'No linked homepage destinations: the menu is left out of the bar'}
      />

      <FooterLinkEditor
        value={menu.footerLink}
        errorPath={`${PATH}.footerLink.url`}
        onChange={(footerLink) => onPatch({ footerLink })}
      />
    </SectionCard>
  );
}
